export interface ClockLocation {
  latitude: number;
  longitude: number;
  outOfBounds?: boolean;
  distance?: number;
}

/**
 * Parses a clock location from JSON string or object into a ClockLocation object
 * Returns null if the location is invalid or null
 *
 * Supports both property name formats:
 * - {lat, lng} from backend
 * - {latitude, longitude} alternative format
 */
export function parseClockLocation(locationData: any): ClockLocation | null {
  if (!locationData || locationData === undefined || locationData === null) {
    return null;
  }

  try {
    // Handle both JSON string and direct object
    let parsed: any;
    if (typeof locationData === "string") {
      if (locationData.trim() === "") return null;
      parsed = JSON.parse(locationData);
    } else if (typeof locationData === "object") {
      parsed = locationData;
    } else {
      return null;
    }

    // GraphQL scalar may wrap JSONB in a 'v' property
    if (parsed.v && typeof parsed.v === "object") {
      parsed = parsed.v;
    }

    // Check for both lat/lng and latitude/longitude property names
    const lat = parsed.latitude ?? parsed.lat;
    const lng = parsed.longitude ?? parsed.lng;

    if (typeof lat === "number" && typeof lng === "number") {
      return {
        latitude: lat,
        longitude: lng,
        outOfBounds: parsed.outOfBounds === true || parsed.out_of_bounds === true,
        distance: typeof parsed.distance === "number" ? parsed.distance : undefined,
      };
    }

    return null;
  } catch (error) {
    console.error("[parseClockLocation] Parse error:", error, "Input:", locationData);
    return null;
  }
}

/**
 * Checks if a clock type is mobile (vs fixed location like office)
 */
export function isMobileClockType(clockType: string | null): boolean {
  if (!clockType) {
    return false;
  }

  const lowerType = clockType.toLowerCase();
  return lowerType.includes("mobile") || lowerType.includes("gps") || lowerType.includes("phone");
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * Returns distance in miles
 */
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3959; // Earth's radius in miles
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in miles
}

/**
 * Check if clock location is out of bounds (> 0.186 miles / 300m from patient address)
 * Returns updated ClockLocation with outOfBounds and distance fields (in miles)
 */
export function checkLocationBounds(
  clockLocation: ClockLocation,
  patientAddressLocation: any
): ClockLocation {
  // Parse patient address location
  let patientLocation: { lat: number; lng: number } | null = null;

  if (patientAddressLocation) {
    try {
      let parsed: any;
      if (typeof patientAddressLocation === "string") {
        parsed = JSON.parse(patientAddressLocation);
      } else {
        parsed = patientAddressLocation;
      }

      // Handle GraphQL's {v: ...} wrapper
      if (parsed.v && typeof parsed.v === "object") {
        parsed = parsed.v;
      }

      const lat = parsed.latitude ?? parsed.lat;
      const lng = parsed.longitude ?? parsed.lng;

      if (typeof lat === "number" && typeof lng === "number") {
        patientLocation = { lat, lng };
      }
    } catch (error) {
      console.error("[checkLocationBounds] Failed to parse patient address:", error);
    }
  }

  // If no patient location, can't determine bounds
  if (!patientLocation) {
    return clockLocation;
  }

  // Calculate distance in miles
  const distance = calculateDistance(
    clockLocation.latitude,
    clockLocation.longitude,
    patientLocation.lat,
    patientLocation.lng
  );

  // Check if out of bounds (> 0.186 miles, which is 300 meters)
  const GEOFENCE_RADIUS = 0.186; // miles (approximately 300 meters)
  const outOfBounds = distance > GEOFENCE_RADIUS;

  return {
    ...clockLocation,
    outOfBounds,
    distance,
  };
}
